import * as Sentry from '@sentry/vue'

const STORAGE_LAST_RELOAD_AT = 'lucimark.pb.unhandledErrorReloadAt'
const STORAGE_RELOAD_COUNT = 'lucimark.pb.unhandledErrorReloadCount'

/** Minimum gap between unhandled-error recovery reloads. */
const RELOAD_COOLDOWN_MS = 2 * 60 * 1000
/** Cap reloads per browser tab session. */
const MAX_SESSION_RELOADS = 3
const FLUSH_MS = 1500
/** Collapse duplicate `error` / `onerror` deliveries for the same reason. */
const DEDUPE_WINDOW_MS = 250

const BENIGN_MESSAGE_PATTERNS = [
  /resizeobserver loop/i,
  /script error\.?/i,
  /loading chunk \d+/i,
  /chunkloaderror/i,
  /failed to fetch dynamically imported module/i,
  /abort(ed)?(?:error)?/i,
  /the operation was aborted/i,
  /the play\(\) request was interrupted/i,
  /the fetching process for the media resource was aborted/i
]

export type TUnhandledErrorRecoveryDecision
  = { reload: false, reason: string }
    | { reload: true, reason: string }

let lastRecoveryAttemptKey: string | null = null
let lastRecoveryAttemptAt = 0

/** Test-only: clear session reload counters and in-memory dedupe. */
export function resetUnhandledErrorRecoveryReloadForTesting(): void {
  lastRecoveryAttemptKey = null
  lastRecoveryAttemptAt = 0
  try {
    sessionStorage.removeItem(STORAGE_LAST_RELOAD_AT)
    sessionStorage.removeItem(STORAGE_RELOAD_COUNT)
  }
  catch {
    /* ignore */
  }
}

function messageFromUnknown(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message || reason.name || String(reason)
  }
  if (typeof reason === 'string') {
    return reason
  }
  if (reason && typeof reason === 'object' && 'message' in reason) {
    const m = (reason as { message?: unknown }).message
    if (typeof m === 'string') {
      return m
    }
  }
  try {
    return String(reason)
  }
  catch {
    return 'unknown'
  }
}

export function isBenignUnhandledRecoveryError(reason: unknown): boolean {
  const msg = messageFromUnknown(reason)
  if (!msg.trim()) {
    return true
  }
  return BENIGN_MESSAGE_PATTERNS.some(re => re.test(msg))
}

export function shouldUnhandledErrorRecoveryReload(reason: unknown, now = Date.now()): TUnhandledErrorRecoveryDecision {
  if (isBenignUnhandledRecoveryError(reason)) {
    return { reload: false, reason: 'benign' }
  }
  let lastAt = 0
  let count = 0
  try {
    lastAt = Number(sessionStorage.getItem(STORAGE_LAST_RELOAD_AT) || '0')
    count = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0')
  }
  catch {
    /* treat as fresh session */
  }
  if (count >= MAX_SESSION_RELOADS) {
    return { reload: false, reason: 'max_session_reloads' }
  }
  if (lastAt > 0 && now - lastAt < RELOAD_COOLDOWN_MS) {
    return { reload: false, reason: 'cooldown' }
  }
  return { reload: true, reason: 'unhandled_error' }
}

/**
 * Rate-limited WebView reload for uncaught errors / rejections that leave playback wedged.
 * Skips benign browser noise; capped per tab session.
 * Dedupes same-reason calls within {@link DEDUPE_WINDOW_MS} (browsers may deliver both
 * `window.addEventListener('error')` and `window.onerror` for one exception).
 */
export function maybePerformUnhandledErrorRecoveryReload(reason: unknown): boolean {
  const detail = messageFromUnknown(reason).slice(0, 200)
  const attemptAt = Date.now()
  if (
    lastRecoveryAttemptKey === detail
    && attemptAt - lastRecoveryAttemptAt < DEDUPE_WINDOW_MS
  ) {
    return false
  }
  lastRecoveryAttemptKey = detail
  lastRecoveryAttemptAt = attemptAt

  const decision = shouldUnhandledErrorRecoveryReload(reason)
  if (!decision.reload) {
    return false
  }

  const now = Date.now()
  let reloadCount = 1
  try {
    reloadCount = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0') + 1
    sessionStorage.setItem(STORAGE_RELOAD_COUNT, String(reloadCount))
    sessionStorage.setItem(STORAGE_LAST_RELOAD_AT, String(now))
  }
  catch {
    /* continue */
  }

  try {
    Sentry.addBreadcrumb({
      category: 'player_unhandled_error_recovery',
      message: 'reload',
      level: 'warning',
      data: { reason: decision.reason, reloadCount, detail }
    })
    Sentry.captureMessage('Player unhandled error recovery reload', {
      level: 'warning',
      tags: { player_unhandled_error_recovery_reload: 'true' },
      fingerprint: ['player-unhandled-error-recovery-reload'],
      extra: { reason: decision.reason, reloadCount, detail: messageFromUnknown(reason).slice(0, 500) }
    })
  }
  catch {
    /* Sentry unavailable */
  }

  const reload = () => {
    window.location.reload()
  }
  try {
    void Sentry.flush(FLUSH_MS).then(reload, reload)
  }
  catch {
    reload()
  }
  return true
}
